import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, PenTool, Eraser, Trash2 } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

function SignPdf() {
  const [file, setFile] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [pageImages, setPageImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [signaturePosition, setSignaturePosition] = useState({ x: 100, y: 100 });
  const [signatureSize, setSignatureSize] = useState({ width: 200, height: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);
  const signatureCanvasRef = useRef(null);
  const contextRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Sign PDF - Add Signature to PDF - KayTool";
  }, []);

  useEffect(() => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      canvas.width = 400;
      canvas.height = 150;
      const context = canvas.getContext('2d');
      context.lineCap = 'round';
      context.strokeStyle = '#000000';
      context.lineWidth = 2;
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      contextRef.current = context;
    }
  }, [file]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setSignatureData(null);
      await loadPdf(selectedFile);
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setSignatureData(null);
      await loadPdf(droppedFile);
    } else {
      toast.error("Please drop a valid PDF file");
    }
  };

  const loadPdf = async (pdfFile) => {
    setIsProcessing(true);
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      setPdfBytes(new Uint8Array(arrayBuffer));
      
      const doc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
      setPdfDoc(doc);
      
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const images = [];
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        await page.render({ canvasContext: context, viewport }).promise;
        images.push({
          dataUrl: canvas.toDataURL(),
          width: viewport.width,
          height: viewport.height
        });
      }
      
      setPageImages(images);
      setCurrentPage(0);
      toast.success(`PDF loaded: ${pdf.numPages} pages`);
    } catch (error) {
      console.error("Error loading PDF:", error);
      toast.error("Failed to load PDF");
    }
    setIsProcessing(false);
  };

  const startDrawing = ({ nativeEvent }) => {
    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.beginPath();
    contextRef.current.moveTo(offsetX, offsetY);
    setIsDrawing(true);
  };

  const draw = ({ nativeEvent }) => {
    if (!isDrawing) return;
    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.lineTo(offsetX, offsetY);
    contextRef.current.stroke();
  };

  const stopDrawing = () => {
    contextRef.current.closePath();
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    const context = contextRef.current;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
  };

  const saveSignature = () => {
    const canvas = signatureCanvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    setSignatureData(dataUrl);
    toast.success("Signature saved! Position it on the PDF.");
  };

  const savePdf = async () => {
    if (!pdfDoc || !signatureData) {
      toast.error("Please draw and save a signature first");
      return;
    }
    
    setIsProcessing(true);
    try {
      // Create fresh PDF document from original bytes
      const newPdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      
      // Convert signature to PNG bytes
      const response = await fetch(signatureData);
      const signatureBytes = await response.arrayBuffer();
      const signatureImage = await newPdfDoc.embedPng(signatureBytes);
      
      const pages = newPdfDoc.getPages();
      const page = pages[currentPage];
      const { height } = page.getSize();
      
      // Calculate scale for signature position
      const scale = pageImages[currentPage] ? 612 / pageImages[currentPage].width * 1.5 : 1;
      
      page.drawImage(signatureImage, {
        x: signaturePosition.x * scale,
        y: height - (signaturePosition.y * scale) - signatureSize.height,
        width: signatureSize.width,
        height: signatureSize.height
      });
      
      const finalPdfBytes = await newPdfDoc.save();
      const blob = new Blob([finalPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name.replace('.pdf', '_signed.pdf');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Signed PDF downloaded!");
    } catch (error) {
      console.error("Error saving PDF:", error);
      toast.error("Failed to save PDF: " + error.message);
    }
    setIsProcessing(false);
  };

  const clearFile = () => {
    setFile(null);
    setPdfDoc(null);
    setPdfBytes(null);
    setPageImages([]);
    setSignatureData(null);
    setCurrentPage(0);
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-teal-50 to-cyan-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
              <PenTool className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-full mb-4">
            <PenTool className="w-5 h-5" />
            <span className="font-medium">Sign PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Sign PDF Documents
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Draw your signature and add it to any PDF document. Free and secure.
          </p>
        </div>

        {/* Upload Area */}
        {!file && (
          <Card 
            className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 max-w-2xl mx-auto ${
              isDragging ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' : 'border-slate-300 dark:border-slate-600'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <CardContent className="p-12 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <div 
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-zone"
              >
                <Upload className="w-16 h-16 mx-auto mb-4 text-teal-500" />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  Drop PDF or click to upload
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Select a PDF file to sign
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {file && (
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* PDF Preview */}
              <div className="lg:col-span-2">
                <Card className="dark:bg-slate-800">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold dark:text-white">
                        Page {currentPage + 1} of {pageImages.length}
                      </h3>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                          disabled={currentPage === 0}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(Math.min(pageImages.length - 1, currentPage + 1))}
                          disabled={currentPage === pageImages.length - 1}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                    
                    {isProcessing ? (
                      <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
                      </div>
                    ) : (
                      <div className="relative bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                        {pageImages[currentPage] && (
                          <img
                            src={pageImages[currentPage].dataUrl}
                            alt={`Page ${currentPage + 1}`}
                            className="w-full h-auto"
                          />
                        )}
                        {/* Signature Preview */}
                        {signatureData && (
                          <div
                            className="absolute border-2 border-dashed border-teal-500 bg-white/80 cursor-move"
                            style={{
                              left: signaturePosition.x,
                              top: signaturePosition.y,
                              width: signatureSize.width,
                              height: signatureSize.height
                            }}
                            draggable
                            onDrag={(e) => {
                              if (e.clientX && e.clientY) {
                                const rect = e.currentTarget.parentElement.getBoundingClientRect();
                                setSignaturePosition({
                                  x: Math.max(0, e.clientX - rect.left - signatureSize.width / 2),
                                  y: Math.max(0, e.clientY - rect.top - signatureSize.height / 2)
                                });
                              }
                            }}
                          >
                            <img
                              src={signatureData}
                              alt="Signature"
                              className="w-full h-full object-contain"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Signature Controls */}
              <div className="space-y-4">
                <Card className="dark:bg-slate-800">
                  <CardContent className="p-4">
                    <h3 className="font-semibold dark:text-white mb-3 flex items-center gap-2">
                      <PenTool className="w-4 h-4" /> Draw Signature
                    </h3>
                    <div className="border border-slate-300 dark:border-slate-600 rounded-lg overflow-hidden bg-white">
                      <canvas
                        ref={signatureCanvasRef}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        className="cursor-crosshair w-full"
                        style={{ touchAction: 'none' }}
                      />
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button variant="outline" size="sm" onClick={clearSignature} className="flex-1">
                        <Eraser className="w-4 h-4 mr-1" /> Clear
                      </Button>
                      <Button size="sm" onClick={saveSignature} className="flex-1 bg-teal-600 hover:bg-teal-700">
                        <PenTool className="w-4 h-4 mr-1" /> Use Signature
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {signatureData && (
                  <Card className="dark:bg-slate-800">
                    <CardContent className="p-4">
                      <h3 className="font-semibold dark:text-white mb-3">Position & Size</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                        Drag the signature on the PDF to position it, or adjust values below:
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-slate-500">X Position</label>
                          <input
                            type="number"
                            value={signaturePosition.x}
                            onChange={(e) => setSignaturePosition({ ...signaturePosition, x: parseInt(e.target.value) || 0 })}
                            className="w-full mt-1 px-2 py-1 border rounded text-sm dark:bg-slate-700 dark:border-slate-600"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Y Position</label>
                          <input
                            type="number"
                            value={signaturePosition.y}
                            onChange={(e) => setSignaturePosition({ ...signaturePosition, y: parseInt(e.target.value) || 0 })}
                            className="w-full mt-1 px-2 py-1 border rounded text-sm dark:bg-slate-700 dark:border-slate-600"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Width</label>
                          <input
                            type="number"
                            value={signatureSize.width}
                            onChange={(e) => setSignatureSize({ ...signatureSize, width: parseInt(e.target.value) || 100 })}
                            className="w-full mt-1 px-2 py-1 border rounded text-sm dark:bg-slate-700 dark:border-slate-600"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Height</label>
                          <input
                            type="number"
                            value={signatureSize.height}
                            onChange={(e) => setSignatureSize({ ...signatureSize, height: parseInt(e.target.value) || 50 })}
                            className="w-full mt-1 px-2 py-1 border rounded text-sm dark:bg-slate-700 dark:border-slate-600"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Button onClick={clearFile} variant="outline" className="flex-1">
                    <X className="w-4 h-4 mr-2" /> Clear
                  </Button>
                  <Button 
                    onClick={savePdf} 
                    disabled={isProcessing || !signatureData}
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600"
                  >
                    <Download className="w-4 h-4 mr-2" /> Save Signed PDF
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default SignPdf;
